import { MessageDecoded } from '@wellz/utils';
import { useIntl } from 'react-intl';
import { getFormattedTime, sanitizeHTML } from 'utils/helpers';

import { MessageBlockProps } from './types';

import * as S from './styles';

const MessageBlock: React.FC<MessageBlockProps> = ({
  titleDate,
  cpIdentity,
  patientTyping,
  patientIdentity,
  messageHandler,
  filteredMessages,
  ...props
}) => {
  const intl = useIntl();
  const filteredDecodedMessages: MessageDecoded[] = filteredMessages.map(
    message => (messageHandler ? messageHandler.readMessage(message) : message),
  );

  const messages = patientTyping
    ? [
        ...filteredDecodedMessages,
        {
          author: patientIdentity,
          decryptedBody: '',
          index: '',
          body: '',
          dateCreated: new Date(),
        },
      ]
    : filteredDecodedMessages;

  const urlRegex = /(\bhttps?:\/\/\S+\b)/gi;

  const convertURLToLink = (text: string) => {
    return text.replace(urlRegex, match => {
      return `<a href="${match}" target="_blank" rel="noopener noreferrer">${match}</a>`;
    });
  };

  return (
    <S.MessageBlockContainer>
      <S.TitleDate {...props}>{titleDate}</S.TitleDate>
      <S.Block data-testid="messages-list">
        {!!messages.length &&
          messages.map((message, index, elements) => {
            const body = message.decryptedBody || message.body;

            const isTypingMessage = !message?.body;
            const hasURL = body && body.match(urlRegex);

            return (
              <S.Message
                $isCpMessage={cpIdentity === message?.author}
                $messageStart={elements[index - 1]?.author !== message.author}
                $messageEnd={elements[index + 1]?.author !== message.author}
                key={
                  isTypingMessage
                    ? 'patientTyping'
                    : titleDate + message.author + message.index + message.body
                }
              >
                {!isTypingMessage && message.dateCreated ? (
                  <>
                    {hasURL ? (
                      <S.MessageBody
                        dangerouslySetInnerHTML={sanitizeHTML(
                          convertURLToLink(body || ''),
                          { ADD_TAGS: ['a'], ADD_ATTR: ['target'] },
                        )}
                      />
                    ) : (
                      <S.MessageBody>{body}</S.MessageBody>
                    )}
                    <S.MessageTime>
                      {getFormattedTime(message.dateCreated.toISOString(), {
                        locale: intl.locale,
                      })}
                    </S.MessageTime>
                  </>
                ) : (
                  <S.DotsTyping data-testid="dotsTyping" />
                )}
              </S.Message>
            );
          })}
      </S.Block>
    </S.MessageBlockContainer>
  );
};

export default MessageBlock;
